import React, { useEffect, useState, useMemo } from 'react';
import { FormControl, TextField, InputLabel, Select, MenuItem, Autocomplete, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../../redux/api';
import { stringifyJSONAttributes, stringToBooleanConversion, encryptString, decryptString } from '../../../../utils/utilFunctions';
import TPCheckbox from '../../../TemplateProperties/TPCheckbox';
import SmartPasswordField from '../../../../commonComponents/SmartPasswordField';
import WorkflowSessionModal from '../../../ItemProperties/WorkflowSessionModal';
import { MoreHorizOutlined } from '@mui/icons-material';

const Settings = ({
    connectionDetails,
    direction,
    disableControl,
    screen,
    onInputChange,
    account,
    repoPDC,
    useConnection,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { rightTree } = useSelector((state) => state.treeData[state.treeData.currentScreen]);
    const currentTab = rightTree.currentTab;
    const selectedNode = rightTree.tree[currentTab].selectedNode;

    const [outTime, setOutTime] = useState('');
    const [sslVersion, setSslVersion] = useState('');
    const [sslVersionsTypeOptions, setSslVersionsTypeOptions] = useState([]);
    const [sslHost, setSslHost] = useState(false);
    const [syncResponse, setSyncResponse] = useState(true);
    const [urlEncode, setUrlEncode] = useState(true);
    const [decompression, setDecompression] = useState(false);
    const [showDecompressionDir, setShowDecompressionDir] = useState(false);
    const [decompressionDir, setDecompressionDir] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [decomPassword, setDecomPassword] = useState('');
    const [compression, setCompression] = useState(false);
    const [compressionList, setCompressionList] = useState([]);
    const [compressionType, setCompressionType] = useState('');
    const [fileNameZip, setFileNameZip] = useState('');
    const [showDestinationPassword, setShowDestinationPassword] = useState(false);
    const [destinationPassword, setDestinationPassword] = useState('');
    const [disableCompression, setDisableCompression] = useState(false);
    const [valueLabel, setValueLabel] = useState('');
    const [workflowSessionModal, setWorkflowSessionModal] = useState({ open: false, isFirstTime: false });

    const getFontWeight = (control, currentValue) => {
        if (!repoPDC) return "normal";
        return repoPDC?.[control] !== currentValue && useConnection === "preDefined" ? "bold" : "normal";
    };

    const fontWeights = useMemo(() => ({
        outTime: [600, "600"].includes(outTime) ? "normal" : getFontWeight("HTTP_TIMEOUT", outTime),
        sslVersion: sslVersion === t('SELECT') ? "normal" : getFontWeight("SSL_TYPE", sslVersion),
        fileNameZip: getFontWeight("FILE_NAME_WITHIN_ZIP", fileNameZip),
        decomPassword: getFontWeight("ZIP_PASSWORD", decomPassword),
        destinationPassword: getFontWeight("ZIP_PASSWORD", destinationPassword),
        decompressionDir: getFontWeight("DECOMPRESSION_DIRECTORY", decompressionDir),
        compressionType: getFontWeight("COMPRESSION_TYPE", compressionType),
    }), [outTime, sslVersion, fileNameZip, decomPassword, destinationPassword, decompressionDir, compressionType, repoPDC, useConnection]);

    const workflowMappings = {
        [t("FILE_NAME_WITHIN_ZIP")]: {
            stateSetter: setFileNameZip,
            key: "FILE_NAME_WITHIN_ZIP",
            value: fileNameZip,
        },
        [t("LBL_ZIP_PASSWORD")]: {
            stateSetter: setDecomPassword,
            key: "ZIP_PASSWORD",
            value: decomPassword,
        },
        [t("LBL_ZIP_PASSWORD") + "_DEST"]: {
            stateSetter: setDestinationPassword,
            key: "ZIP_PASSWORD",
            value: destinationPassword,
        },
    };

    const valuesHandler = (value) => {
        if (workflowMappings[valueLabel]) {
            const { stateSetter, key } = workflowMappings[valueLabel];
            if (key === 'ZIP_PASSWORD') {
                const encryptedValue = encryptString(value, process.env.REACT_APP_SECRET_KEY2);
                stateSetter(value);
                onInputChange(key, encryptedValue);
            } else {
                stateSetter(value);
                onInputChange(key, value);
            }
        }
    };

    const handleCompressionTypeChange = (value) => {
        setCompressionType(value);
        if (["GZ", "TAR", "TGZ"].includes(value)) {
            setShowDestinationPassword(false);
            setDestinationPassword("");
            if (screen && screen === "SEND_DATA") {
                onInputChange("add", { ZIP: value, USE_ZIP_PASSWORD: "FALSE", ZIP_PASSWORD: null });
            } else {
                onInputChange("ZIP", value);
                onInputChange("USE_ZIP_PASSWORD", stringifyJSONAttributes(false));
                onInputChange("ZIP_PASSWORD", "");
            }
        } else {
            onInputChange("ZIP", value);
        }
    };

    useEffect(() => {
        dispatch(api.endpoints.getSslVersion.initiate({ account })).then((res) => {
            if (res?.data?.current_state === 'SUCCESS') {
                setSslVersionsTypeOptions(res?.data?.data);
            }
        });

        if (direction === 'OUT') {
            dispatch(api.endpoints.getCompressionList.initiate()).then((res) => {
                if (res?.data?.current_state === 'SUCCESS') {
                    const types = Object.values(res?.data?.data)?.map((item) => item?.TYPE);
                    setCompressionList(types);
                }
            });
        }
    }, [direction]);

    useEffect(() => {
        setOutTime(connectionDetails?.HTTP_TIMEOUT || 600);
        setSslVersion(connectionDetails?.SSL_TYPE || t('SELECT'));
        setSslHost(stringToBooleanConversion(connectionDetails?.FORCE_HOST_VERIFICATION) || false);
        setSyncResponse(stringToBooleanConversion(connectionDetails?.SYNCHRONOUS) || false);
        setUrlEncode(connectionDetails?.URLENCODE?.toLowerCase() === "false" ? false : true);
        if (connectionDetails?.ZIP) {
            direction === 'IN' ? setDecompression(true) : setCompression(true);
        } else {
            setDecompression(false);
            setCompression(false);
        }
        setDecompressionDir(connectionDetails?.DECOMPRESSION_DIRECTORY || '');
        if (
            (connectionDetails?.DECOMPRESSION_DIRECTORY && connectionDetails?.DECOMPRESSION_DIRECTORY?.length !== 0) ||
            stringToBooleanConversion(connectionDetails?.SPECIFY_DECOMPRESSION_DIRECTORY)
        ) {
            setShowDecompressionDir(true);
        } else {
            setShowDecompressionDir(false);
        }
        setShowPassword(stringToBooleanConversion(connectionDetails?.USE_ZIP_PASSWORD));
        setDecomPassword(connectionDetails?.ZIP_PASSWORD ? decryptString(connectionDetails?.ZIP_PASSWORD, process.env.REACT_APP_SECRET_KEY2) : '');
        setFileNameZip(connectionDetails?.FILE_NAME_WITHIN_ZIP || '');
        if (direction === 'OUT') {
            setCompressionType(connectionDetails?.ZIP || 'ZIP');
            setShowDestinationPassword(stringToBooleanConversion(connectionDetails?.USE_ZIP_PASSWORD));
            setDestinationPassword(connectionDetails?.ZIP_PASSWORD ? decryptString(connectionDetails?.ZIP_PASSWORD, process.env.REACT_APP_SECRET_KEY2) : '');
        }
    }, [connectionDetails]);

    useEffect(() => {
        if (
            selectedNode?.INBOUND?.DONT_RETAIN_PLAIN_TEXT === "TRUE" ||
            (selectedNode?.INBOUND?.hasOwnProperty("ENCRYPTION_TYPE") && selectedNode?.INBOUND?.ENCRYPTION_TYPE !== "NONE") ||
            selectedNode?.OUTBOUND?.DONT_RETAIN_PLAIN_TEXT === "TRUE" ||
            (selectedNode?.OUTBOUND?.hasOwnProperty("ENCRYPTION_TYPE") && selectedNode?.OUTBOUND?.ENCRYPTION_TYPE !== "NONE")
        ) {
            setDisableCompression(true);
        } else {
            setDisableCompression(false);
        }
    }, [selectedNode?.INBOUND, selectedNode?.OUTBOUND]);

    return (
        <>
            <FormControl className="width-100" size="small">
                <TextField
                    variant="outlined"
                    size="small"
                    label={t("LBL_TIMEOUT")}
                    type="text"
                    value={outTime}
                    disabled={disableControl || false}
                    inputProps={{ style: { fontWeight: fontWeights.outTime } }}
                    onChange={(e) => {
                        const numericValue = e.target.value.replace(/\D/g, '');
                        if (numericValue.length <= 9) {
                            setOutTime(numericValue);
                            onInputChange(e.target.name, numericValue);
                        }
                    }}
                    className={"width-100"}
                    name={"HTTP_TIMEOUT"}
                    autoComplete="off"
                    sx={{ margin: '15px 0' }}
                />
            </FormControl>

            <FormControl variant="outlined" className="width-100" size="small">
                <InputLabel id="sslTypeDropdownLabel">
                    {t("LBL_SSL_TYPE")}
                </InputLabel>
                <Select
                    className={"width-100"}
                    disabled={disableControl || false}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    name={"SSL_TYPE"}
                    labelId="sslTypeDropdownLabel"
                    label={t("LBL_SSL_TYPE")}
                    id="sslTypeDropdown"
                    value={sslVersion || ''}
                    sx={{ "& .MuiSelect-select": { fontWeight: fontWeights.sslVersion } }}
                    onChange={(e) => {
                        onInputChange(e.target.name, e.target.value);
                        setSslVersion(e.target.value);
                    }}
                >
                    {sslVersionsTypeOptions.map((obj, key) => (
                        <MenuItem key={key} value={obj}>{obj}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))' }}>
                <TPCheckbox
                    label={t("BTN_FORCE_HOST_VERIFICATION")}
                    checked={sslHost}
                    disabled={disableControl || false}
                    handleCheck={(checked) => {
                        onInputChange('FORCE_HOST_VERIFICATION', stringifyJSONAttributes(checked));
                        setSslHost(checked);
                    }}
                />
                <TPCheckbox
                    label={direction === 'IN' ? t("BTN_SEND_SYNC_RESPONSE") : t("BTN_RECEIVE_SYNC_RESPONSE")}
                    checked={syncResponse}
                    disabled={disableControl || false}
                    handleCheck={(checked) => {
                        onInputChange('SYNCHRONOUS', stringifyJSONAttributes(checked));
                        setSyncResponse(checked);
                    }}
                />
                <TPCheckbox
                    label={t("BTN_URLENCODE")}
                    checked={urlEncode}
                    disabled={disableControl || false}
                    handleCheck={(checked) => {
                        onInputChange('URLENCODE', stringifyJSONAttributes(checked));
                        setUrlEncode(checked);
                    }}
                />
                {direction === 'IN' && (
                    <TPCheckbox
                        label={t("BTN_USE_ZIP_DECOMPRESS")}
                        checked={decompression}
                        disabled={disableControl || disableCompression || false}
                        handleCheck={(checked) => {
                            onInputChange('ZIP', checked ? 'ZIP' : null);
                            setDecompression(checked);
                            if (!checked) {
                                setShowDecompressionDir(false);
                                onInputChange(null, null, { DECOMPRESSION_DIRECTORY: null, ZIP: null });
                                setDecompressionDir('');
                            }
                        }}
                    />
                )}
                {direction === 'OUT' && (
                    <TPCheckbox
                        label={t("BTN_USE_ZIP_COMPRESS")}
                        checked={compression}
                        disabled={disableControl || disableCompression || false}
                        handleCheck={(checked) => {
                            onInputChange("ZIP", checked ? "ZIP" : null);
                            setCompression(checked);
                            checked && setCompressionType("ZIP");
                            if (!checked) {
                                setCompressionType('ZIP');
                                // onInputChange('COMPRESSION_TYPE', 'ZIP');
                                // setFileNameZip('');
                                // onInputChange('FILE_NAME_WITHIN_ZIP', '');
                                // setShowDestinationPassword(false);
                                // onInputChange('USE_ZIP_PASSWORD', stringifyJSONAttributes(false));
                                // setDestinationPassword('');
                                // onInputChange('ZIP_PASSWORD', '');
                                setFileNameZip("");
                                onInputChange(null, null, { ZIP: null, FILE_NAME_WITHIN_ZIP: null });
                            }
                        }}
                    />
                )}
            </div>

            {decompression && direction === 'IN' && (
                <>
                    <div>
                        <TPCheckbox
                            label={t("LBL_SPECIFY_DECOMPRESSION_DIRECTORY")}
                            checked={showDecompressionDir}
                            disabled={disableControl || false}
                            handleCheck={(checked) => {
                                setShowDecompressionDir(checked);
                                if (!checked) {
                                    onInputChange('DECOMPRESSION_DIRECTORY', '');
                                    setDecompressionDir('');
                                }
                            }}
                        />
                    </div>
                    {showDecompressionDir && (
                        <FormControl className="width-100" size="small">
                            <TextField
                                variant="outlined"
                                size="small"
                                label={t("LBL_DECOMPRESS_DIRECTORY")}
                                type="text"
                                value={decompressionDir || ''}
                                disabled={disableControl || false}
                                inputProps={{ style: { fontWeight: fontWeights.decompressionDir } }}
                                onChange={(e) => {
                                    onInputChange(e.target.name, e.target.value);
                                    setDecompressionDir(e.target.value);
                                }}
                                className={"width-100"}
                                name={"DECOMPRESSION_DIRECTORY"}
                                autoComplete="off"
                            />
                        </FormControl>
                    )}
                    <div>
                        <TPCheckbox
                            label={t("LBL_WS_PASSWORD")}
                            checked={showPassword}
                            disabled={disableControl || false}
                            handleCheck={(checked) => {
                                onInputChange('USE_ZIP_PASSWORD', stringifyJSONAttributes(checked));
                                setShowPassword(checked);
                                if (!checked) {
                                    setDecomPassword('');
                                    onInputChange('ZIP_PASSWORD', '');
                                }
                            }}
                        />
                    </div>
                    {showPassword && (
                        <div style={{ display: "flex", gap: "4px" }}>
                            <SmartPasswordField
                                variant="outlined"
                                size="small"
                                label={t("LBL_WS_PASSWORD")}
                                value={decomPassword || ''}
                                disabled={disableControl || false}
                                inputProps={{ style: { fontWeight: fontWeights.decomPassword } }}
                                onChange={(e) => {
                                    onInputChange(e.target.name, encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2));
                                    setDecomPassword(e.target.value);
                                }}
                                className={"width-100"}
                                name={"ZIP_PASSWORD"}
                                autoComplete="off"
                            />
                            {["GET_DATA", "SEND_DATA"].includes(screen) && (
                                <WorkflowSessionModalButton
                                    disableControl={disableControl || false}
                                    label={t("LBL_ZIP_PASSWORD")}
                                    setWorkflowSessionModal={setWorkflowSessionModal}
                                    setValueLabel={setValueLabel}
                                />
                            )}
                        </div>
                    )}
                </>
            )}

            {compression && direction === 'OUT' && (
                <>
                    <Autocomplete
                        disablePortal
                        options={compressionList}
                        getOptionLabel={(option) => option}
                        value={compressionType}
                        disabled={disableControl || false}
                        sx={{ maxHeight: 300, margin: "15px 0" }}
                        onChange={(e, value) => {
                            handleCompressionTypeChange(value);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("LBL_COMPRESSION_TYPE")}
                                inputProps={{
                                    ...params.inputProps,
                                    style: { fontWeight: fontWeights.compressionType }
                                }}
                                onChange={(e) => {
                                    handleCompressionTypeChange(e.target.value);
                                }}
                                variant="outlined"
                                size="small"
                                name={'ZIP'}
                                autoComplete="off"
                            />
                        )}
                    />
                    <div style={{ display: "flex", gap: "4px" }}>
                        <FormControl className="width-100" size="small">
                            <TextField
                                variant="outlined"
                                size="small"
                                label={t("FILE_NAME_WITHIN_ZIP")}
                                type="text"
                                value={fileNameZip || ''}
                                disabled={disableControl || false}
                                inputProps={{ style: { fontWeight: fontWeights.fileNameZip } }}
                                onChange={(e) => {
                                    onInputChange(e.target.name, e.target.value);
                                    setFileNameZip(e.target.value);
                                }}
                                className={"width-100"}
                                name={"FILE_NAME_WITHIN_ZIP"}
                                autoComplete="off"
                            />
                        </FormControl>
                        {["GET_DATA", "SEND_DATA"].includes(screen) && (
                            <WorkflowSessionModalButton
                                disableControl={disableControl || false}
                                label={t("FILE_NAME_WITHIN_ZIP")}
                                setWorkflowSessionModal={setWorkflowSessionModal}
                                setValueLabel={setValueLabel}
                            />
                        )}
                    </div>
                    {['JAR', 'RAR', 'ZIP'].includes(compressionType) && (
                        <>
                            <div>
                                <TPCheckbox
                                    label={t("LBL_WS_PASSWORD")}
                                    checked={showDestinationPassword}
                                    disabled={disableControl || false}
                                    handleCheck={(checked) => {
                                        onInputChange('USE_ZIP_PASSWORD', stringifyJSONAttributes(checked));
                                        setShowDestinationPassword(checked);
                                        if (!checked) {
                                            onInputChange('ZIP_PASSWORD', '');
                                            setDestinationPassword('');
                                        }
                                    }}
                                />
                            </div>
                            {showDestinationPassword && (
                                <div style={{ display: "flex", gap: "4px" }}>
                                    <SmartPasswordField
                                        variant="outlined"
                                        size="small"
                                        type="password"
                                        label={t("LBL_WS_PASSWORD")}
                                        value={destinationPassword || ''}
                                        disabled={disableControl || false}
                                        inputProps={{ style: { fontWeight: fontWeights.destinationPassword } }}
                                        onChange={(e) => {
                                            onInputChange(e.target.name, encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2));
                                            setDestinationPassword(e.target.value);
                                        }}
                                        className={"width-100"}
                                        name={"ZIP_PASSWORD"}
                                        autoComplete="off"
                                    />
                                    {["GET_DATA", "SEND_DATA"].includes(screen) && (
                                        <WorkflowSessionModalButton
                                            disableControl={disableControl || false}
                                            label={t("LBL_ZIP_PASSWORD") + "_DEST"}
                                            setWorkflowSessionModal={setWorkflowSessionModal}
                                            setValueLabel={setValueLabel}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            <WorkflowSessionModal
                valueLabel={valueLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowSessionModal}
                setModalOpen={setWorkflowSessionModal}
                handleClose={() => setWorkflowSessionModal({ open: false, isFirstTime: false })}
                initialValue={workflowMappings[valueLabel]?.value}
                inputType={[t("LBL_ZIP_PASSWORD"), t("LBL_ZIP_PASSWORD") + "_DEST"].includes(valueLabel) ? "password" : "text"}
                specialPasswordField={[t("LBL_ZIP_PASSWORD"), t("LBL_ZIP_PASSWORD") + "_DEST"].includes(valueLabel)}
                valuesHandler={valuesHandler}
            />
        </>
    );
};

export default Settings;

const WorkflowSessionModalButton = ({ setWorkflowSessionModal, label, disableControl, setValueLabel }) => {
    return (
        <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
                setValueLabel(label);
                setWorkflowSessionModal({ open: true, isFirstTime: true });
            }}
            sx={{ width: "40px", minWidth: "40px" }}>
            <MoreHorizOutlined fontSize="small" />
        </Button>
    );
};
